import { Formik } from 'formik';
import CustomModal from '../../../components/CustomModal';
import { Button, Dropdown, InputField, Text } from '../../../components/SecondaryMarket';
import ViewReverse from '../../../components/ViewReverse';
import { ReactSVG } from 'react-svg';
import icons from '../../../constants/icons';
import { fontSizes } from '../../../constants/constants';
import dynamicfunction from '../../../utils/dynamicfunction';
import { bank_details_validation_schema } from '../../../validations_schemas/bank_and_custodian_information_validation_schema';
import { alphabet_numeric_regix, numeric_integer_regix } from '../../../utils/functions';

const BankEditModal = ({
  isOpen,
  onClose,
  getContentKey,
  getEditBankInitialValues,
  handleEditBankSubmit,
  corporate_dropdown_data,
  corporate_loading,
  isKYC
}) => {
  const { TextWeight } = dynamicfunction();

  return (
    <CustomModal isOpen={isOpen} onClose={onClose} maxWidth={'lg'} heading={getContentKey('edit_bank')}>
      {isOpen && (
        <Formik
          enableReinitialize
          initialValues={getEditBankInitialValues()}
          validationSchema={bank_details_validation_schema}
          onSubmit={handleEditBankSubmit}
          validateOnChange={false}
          validateOnMount={false}
          validateOnBlur={false}
        >
          {({ values: bankValues, errors: bankErrors, setFieldValue: setBankFieldValue, handleSubmit, resetForm: resetBankForm }) => {
            const isDisabled = !bankValues?.bankTypeId;
            const is_ksa = bankValues?.bankTypeId === 1061;

            return (
              <div className='bg-light-background p-6 rounded-lg mt-4'>
                <Text text={getContentKey('bank_details')} className={'text-light-blue'} size={fontSizes.h22} />

                <div className='space-y-6'>
                  <ViewReverse className={'gap-6 mt-8'} isResponsive>
                    {bankValues?.bankTypeId === 1061 ? (
                      <Dropdown
                        label={getContentKey('bank')}
                        valueKey={'name'}
                        value={bankValues?.bankId ? corporate_dropdown_data?.banks?.find((v) => v?.id === bankValues?.bankId) : null}
                        isRequired={false}
                        options={corporate_dropdown_data?.banks}
                        onSelect={(val) => setBankFieldValue('bankId', val?.id)}
                        error={getContentKey(bankErrors?.bankId)}
                        isDisabled={isDisabled}
                      />
                    ) : (
                      <InputField
                        label={getContentKey('bank_name')}
                        isRequired
                        placeholder={getContentKey('enter_bank_name')}
                        className={'bg-white'}
                        value={bankValues?.bankName}
                        onChange={(text) => setBankFieldValue('bankName', text)}
                        error={getContentKey(bankErrors?.bankName)}
                        isDisabled={isDisabled}
                      />
                    )}
                    <InputField
                      label={getContentKey('branch')}
                      isRequired
                      placeholder={getContentKey('enter_branch')}
                      className={'bg-white'}
                      value={bankValues?.branch}
                      onChange={(text) => setBankFieldValue('branch', text)}
                      error={getContentKey(bankErrors?.branch)}
                      isDisabled={isDisabled}
                    />
                    <InputField
                      label={getContentKey('beneficiary_name')}
                      isRequired
                      placeholder={getContentKey('enter_beneficiary_name')}
                      className={'bg-white'}
                      value={bankValues?.beneficiaryName}
                      onChange={(text) => setBankFieldValue('beneficiaryName', text)}
                      error={getContentKey(bankErrors?.beneficiaryName)}
                      isDisabled={isKYC ? true : isDisabled}
                      // isDisabled={isDisabled}
                    />
                  </ViewReverse>

                  <ViewReverse className={'gap-6 mt-8'} isResponsive>
                    <InputField
                      label={getContentKey('iban')}
                      isRequired
                      inputClasses={'uppercase'}
                      placeholder={getContentKey('enter_iban')}
                      className={'bg-white'}
                      value={bankValues?.iban}
                      onChange={(text) => {
                        let value = text.toUpperCase();
                        if (!text.startsWith('SA') && is_ksa) {
                          value = 'SA';
                        }
                        if (alphabet_numeric_regix.test(text)) {
                          setBankFieldValue('iban', value)
                        }
                      }
                      }
                      error={getContentKey(bankErrors?.iban)}
                      maxLength={!is_ksa ? 34 : 24}
                      isDisabled={isDisabled}
                    />

                    {bankValues?.bankTypeId === 1062 ? (
                      <>
                        <InputField
                          label={getContentKey('account_number')}
                          isRequired
                          placeholder={getContentKey('enter_account_number')}
                          className={'bg-white'}
                          value={bankValues?.accountNumber}
                          onChange={(text) => setBankFieldValue('accountNumber', text)}
                          error={getContentKey(bankErrors?.accountNumber)}
                          isDisabled={isDisabled}
                          maxLength={34}
                        />
                        <InputField
                          label={getContentKey('registered_address')}
                          isRequired
                          placeholder={getContentKey('enter_registered_address')}
                          className={'bg-white'}
                          value={bankValues?.registeredAddress}
                          onChange={(text) => setBankFieldValue('registeredAddress', text)}
                          error={getContentKey(bankErrors?.registeredAddress)}
                          isDisabled={isDisabled}
                        />
                      </>
                    ) : (
                      <>
                        <div className='w-full hidden md:flex' />
                        <div className='w-full hidden md:flex' />
                      </>
                    )}
                  </ViewReverse>

                  {bankValues?.bankTypeId === 1062 && (
                    <ViewReverse className={'gap-6 mt-8'} isResponsive>
                      <InputField
                        label={getContentKey('aba_routing_number')}
                        isRequired
                        placeholder={getContentKey('enter_aba_routing_number')}
                        className={'bg-white'}
                        maxLength={9}
                        value={bankValues?.abaRoutingNumber}
                        onChange={(text) => {
                          if (numeric_integer_regix.test(text)) {
                            setBankFieldValue('abaRoutingNumber', text)
                          }
                        }}
                        error={getContentKey(bankErrors?.abaRoutingNumber)}
                      />
                      <InputField
                        label={getContentKey('swift_code')}
                        isRequired
                        placeholder={getContentKey('enter_swift_code')}
                        className={'bg-white'}
                        maxLength={11}
                        inputClasses={'uppercase'}
                        value={bankValues?.swiftCode}
                        onChange={(text) => setBankFieldValue('swiftCode', text)}
                        error={getContentKey(bankErrors?.swiftCode)}
                      />
                      <div className='w-full hidden md:flex' />
                    </ViewReverse>
                  )}
                </div>

                <ViewReverse className={'gap-2 mt-10'}>
                  <ReactSVG src={icons.info_red_icon} />
                  <Text text={getContentKey('atleast_one_bank_required')} className={'text-red-600'} />
                </ViewReverse>
                <ViewReverse className={'gap-2 mt-4'}>
                  <ReactSVG src={icons.info_red_icon} />
                  <Text text={getContentKey('max_five_accounts')} className={'text-red-600'} />
                </ViewReverse>
                <ViewReverse className={'gap-2 mt-4'}>
                  <ReactSVG src={icons.info_red_icon} />
                  <Text text={getContentKey('beneficiary_name_validation')} className={'text-red-600'} />
                </ViewReverse>

                <ViewReverse className={'w-full mt-5 justify-end gap-2'} isResponsive>
                  <Button
                    text={getContentKey('cancel')}
                    className={'w-full md:w-44 bg-white border border-grey text-black'}
                    weight={TextWeight.bold}
                    textSize={fontSizes.h14}
                    onClick={() => (resetBankForm({ values: getEditBankInitialValues() }), onClose())}
                  />
                  <Button
                    text={getContentKey('edit')}
                    className={'w-full md:w-44 bg-light-blue border-none'}
                    weight={TextWeight.bold}
                    textSize={fontSizes.h14}
                    onClick={handleSubmit}
                    loading={corporate_loading}
                    isDisabled={corporate_loading}
                  />
                </ViewReverse>
              </div>
            );
          }}
        </Formik>
      )}
    </CustomModal>
  );
};

export default BankEditModal;
